import { fetchInvoicePdf } from "../api/mobile";
import {
  docSuccessActionsHtml,
  formActionsHtml,
  panelHeaderHtml,
} from "../components/formActions";
import {
  disclosureBlockHtml,
  dueRuleFieldsHtml,
  formSectionHtml,
  wireDisclosure,
  wireDueRuleToggles,
} from "../components/forms";
import {
  collapsedDueSummaryText,
  duePrefsForNewInvoice,
  dueRuleFromFormData,
  invoiceDueSummary,
} from "../domain/dueDates";
import { confirmSheet, emptyStateHtml, openSheet, showToast, wireSheetGrabDismiss } from "../components/ui";
import {
  abnLookupFieldHtml,
  confirmAbnBeforeSave,
  wireAbnLookup,
} from "../components/abnLookup";
import { formatScheduledAt, openSendTimingSheet } from "../components/sendSheet";
import { trackEvent } from "../lib/analytics";
import { friendlyPdfErrorMessage, isPdfEncodeError } from "../lib/pdfErrors";
import { cache } from "../data/idb";
import {
  cancelScheduledSend,
  flushQueue,
  newInvoiceId,
  pullBootstrap,
  queueEmailSend,
  queueFollowupEmail,
  saveInvoicePackage,
  softDeleteInvoice,
  updateInvoiceStatus,
  upsertCustomer,
  watchDocSendStatus,
} from "../data/sync";
import {
  addressFieldsHtml,
  normalizeAbn,
  normalizeAuAddress,
  readAddressFromForm,
} from "../domain/address";
import {
  anyBusinessGstRegistered,
  lineItemsSummary,
  parseLineItems,
  previewLineAmounts,
  unitIncFromEx,
} from "../domain/gst";
import {
  formatInvoiceDate,
  formatInvoiceNumber,
  formatMoney,
  moneyStr,
  parseInvoiceNumberInput,
  suggestedInvoiceNumber,
  todayIso,
} from "../domain/invoiceFormat";
import {
  findInvoiceNumberConflicts,
  invoiceStorageKey,
} from "../domain/invoiceIdentity";
import {
  invoiceSummaryCardHtml,
} from "../components/docCards";
import {
  clearDocReturnTo,
  navigateDocReturnOr,
  openInvoiceDoc,
  openQuotesFiltered,
  registerInvoiceListFilter,
  setDocReturnTo,
} from "../domain/crossDocNav";
import {
  countActiveFilters,
  filterInvoices,
  invoicesByStatus,
  statusLabel,
  type InvoiceFilters,
} from "../domain/invoicesGroup";
import { esc } from "../lib/escape";
import { wireDocSuccessPanel, wireFormScreen } from "../lib/formActions";
import { filesToWorkPhotos } from "../lib/images";
import { getDocSuccessMode } from "../lib/navigation";
import { attachCustomGuard } from "../lib/unsaved";
import { router } from "../router";
import type { AppContext } from "../types";

let filterState: InvoiceFilters = {};
let draft: Record<string, unknown> | null = null;
let draftPhotos: string[] = [];
let filtersOpen = false;
/** One-off customers for the current create flow (not persisted to customers list). */
let ephemeralCustomers: Record<string, Record<string, unknown>> = {};
/** Blob URL for the success-screen PDF embed; revoke on leave. */
let successPdfUrl: string | null = null;

type DraftLine = {
  id: string;
  description: string;
  qty: string;
  amount: string;
  gst_applicable: boolean;
  /** When true and GST applicable, `amount` is GST-inclusive. */
  amount_includes_gst: boolean;
};

let draftLines: DraftLine[] = [];

export function openInvoicesFiltered(q: string) {
  filterState = { q: String(q || "").trim() };
  filtersOpen = true;
  router.navigate("invoices");
}

/** Open create with a saved customer pre-selected (from customer profile). */
export function startCreateInvoiceForCustomer(customerName: string) {
  const name = String(customerName || "").trim();
  clearCreateDraft();
  if (name) {
    draft = { customer_name: name };
    setDocReturnTo({ tab: "customers", sub: "view", name });
  } else {
    clearDocReturnTo();
  }
  router.navigate("invoices", "create");
}

registerInvoiceListFilter((q) => {
  filterState = { q };
  filtersOpen = true;
});

function newDraftLineId(): string {
  return `line_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function clearCreateDraft() {
  draft = null;
  draftPhotos = [];
  draftLines = [];
  ephemeralCustomers = {};
}

function isCreateDraftDirty(form: HTMLFormElement | null): boolean {
  if (draftLines.some((l) => l.description.trim() || l.amount.trim())) return true;
  if (draftPhotos.length > 0) return true;
  if (!form) return Boolean(draft?.customer_name || draft?.comment);
  const fd = new FormData(form);
  if (String(fd.get("comment") || "").trim()) return true;
  if (String(fd.get("customer") || "").trim()) return true;
  return false;
}

function leaveCreateForm() {
  clearCreateDraft();
  navigateDocReturnOr(() => router.navigate("invoices", null, {}, { skipPush: true }));
}

function revokeSuccessPdfUrl() {
  if (successPdfUrl) {
    URL.revokeObjectURL(successPdfUrl);
    successPdfUrl = null;
  }
}

/** Persist live create-form fields into `draft` before a remount (e.g. after photos). */
function captureCreateFormState(panel: HTMLElement) {
  const form = panel.querySelector("#create-invoice-form") as HTMLFormElement | null;
  if (!form) return;
  const fd = new FormData(form);
  const customerName = String(fd.get("customer") || "").trim();
  const businessName = String(fd.get("business") || "").trim();
  draft = {
    ...(draft || {}),
    customer_name: customerName || draft?.customer_name || "",
    business_name: businessName || draft?.business_name || "",
    invoice_number: String(fd.get("invoice_number") || draft?.invoice_number || "").trim(),
    invoice_date: String(fd.get("invoice_date") || draft?.invoice_date || "").trim(),
    due_rule_type: String(fd.get("due_rule_type") || draft?.due_rule_type || "").trim(),
    due_net_days: Number(fd.get("due_net_days") ?? draft?.due_net_days ?? 7),
    due_fixed_date: String(fd.get("due_fixed_date") || draft?.due_fixed_date || "").trim(),
    comment: String(fd.get("comment") || "").trim(),
  };
}

function loadInvoiceIntoCreateDraft(inv: Record<string, unknown>) {
  draft = { ...inv };
  draftPhotos = [...((inv.work_photos_b64 as string[]) || [])];
  const items = (inv.line_items as Array<Record<string, unknown>> | undefined) || [];
  draftLines = items.map((item) => {
    const includesGst = Boolean(item.amount_includes_gst) && item.gst_applicable !== false;
    const unitEx = Number(item.unit_amount_ex_gst ?? 0);
    const displayUnit = includesGst ? unitIncFromEx(unitEx) : unitEx;
    return {
      id: newDraftLineId(),
      description: String(item.description || ""),
      qty: String(item.quantity ?? "1"),
      amount: String(item.unit_amount_ex_gst == null ? "" : displayUnit),
      gst_applicable: item.gst_applicable !== false,
      amount_includes_gst: includesGst,
    };
  });
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function fetchPdfBlob(
  invoiceId: string
): Promise<{ blob: Blob; filename: string; url: string }> {
  const key = String(invoiceId || "").trim();
  let lastErr: unknown;
  for (let attempt = 0; attempt < 8; attempt++) {
    if (attempt > 0) await sleep(700);
    try {
      if (attempt > 0) {
        try {
          await pullBootstrap();
        } catch {
          /* fetch may still generate */
        }
      }
      const data = await fetchInvoicePdf(key);
      const binary = atob(data.content_b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const filename = String(data.filename || `Invoice_${key}.pdf`).trim() || "invoice.pdf";
      const url = URL.createObjectURL(blob);
      return { blob, filename, url };
    } catch (ex) {
      const msg = friendlyPdfErrorMessage(ex instanceof Error ? ex.message : String(ex || ""));
      lastErr = new Error(msg || "PDF not ready.");
      if (isPdfEncodeError(msg)) break;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error("PDF not ready.");
}

function downloadPdf(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "invoice.pdf";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function customerFilterNames(
  customers: Record<string, Record<string, unknown>>,
  invoices: Record<string, Record<string, unknown>>
): string[] {
  const names = new Set(Object.keys(customers));
  for (const inv of Object.values(invoices)) {
    if (inv.deleted_at) continue;
    const n = String(inv.customer_name || "").trim();
    if (n) names.add(n);
  }
  return [...names].sort();
}

function resolveCustomerProfile(
  name: string,
  customers: Record<string, Record<string, unknown>>
): Record<string, unknown> {
  return customers[name] || ephemeralCustomers[name] || {};
}

function snapshotCustomerOntoInvoice(
  customerName: string,
  profile: Record<string, unknown>
): Record<string, unknown> {
  return {
    customer_name: customerName,
    customer_email: String(profile.email || "").trim(),
    customer_abn: String(profile.abn || "").trim(),
    address_line1: String(profile.address_line1 || "").trim(),
    address_line2: String(profile.address_line2 || "").trim(),
    suburb: String(profile.suburb || "").trim(),
    state: String(profile.state || "").trim(),
    postcode: String(profile.postcode || "").trim(),
  };
}

/** Customer-facing line total (inc GST when applicable). */
function draftLineAmount(line: DraftLine, gstReg: boolean): number | null {
  const preview = previewLineAmounts({
    amount: line.amount,
    qty: line.qty,
    gstRegistered: gstReg,
    gstApplicable: line.gst_applicable,
    amountIncludesGst: line.amount_includes_gst,
  });
  return preview ? preview.amountInc : null;
}

function readDraftLineRows(gstReg: boolean) {
  return draftLines.map((line) => ({
    description: line.description,
    amount: line.amount,
    qty: line.qty,
    gst_free: gstReg ? !line.gst_applicable : false,
    amount_includes_gst: gstReg && line.gst_applicable && line.amount_includes_gst,
  }));
}

export async function renderInvoices(panel: HTMLElement, ctx: AppContext): Promise<void> {
  if (router.sub === "create") return renderCreate(panel, ctx);
  if (router.sub === "success") return renderSuccess(panel, ctx);

  const [invoices, customers, businesses, settings, quotes] = await Promise.all([
    cache.getInvoices(),
    cache.getCustomers(),
    cache.getBusinesses(),
    cache.getSettings(),
    cache.getQuotes(),
  ]);

  const filtered = filterInvoices(invoices, filterState);
  const filteredMap = Object.fromEntries(
    filtered.map((inv) => [invoiceStorageKey(inv), inv])
  );
  const { groups, sentTotal } = invoicesByStatus(filteredMap, settings);
  const multiBiz = Object.keys(businesses).length > 1;
  const customerNames = customerFilterNames(customers, invoices);
  const businessNames = Object.keys(businesses).sort();
  const activeFilterCount = countActiveFilters(filterState);
  const totalCount =
    groups.not_sent.length + groups.sent.length + groups.paid.length;

  panel.innerHTML = `
    <div class="panel-header">
      <h2>Past invoices</h2>
      <button type="button" class="btn small primary" id="new-invoice">Create</button>
    </div>
    <button type="button" class="btn small secondary filters-toggle" id="toggle-filters">
      Filters${activeFilterCount ? ` · ${activeFilterCount} active` : ""} ▾
    </button>
    <form id="invoice-filters" class="panel filters-panel" ${filtersOpen ? "" : "hidden"}>
      <div class="field"><label>Search</label><input name="q" value="${esc(filterState.q || "")}" placeholder="Number, customer, description"></div>
      <div class="field-row">
        <div class="field"><label>Status</label>
          <select name="status">
            <option value="">All</option>
            <option value="not_sent" ${filterState.status === "not_sent" ? "selected" : ""}>Not sent</option>
            <option value="sent" ${filterState.status === "sent" ? "selected" : ""}>Sent</option>
            <option value="paid" ${filterState.status === "paid" ? "selected" : ""}>Paid</option>
          </select></div>
        <div class="field"><label>Customer</label>
          <select name="customer"><option value="">All</option>
            ${customerNames
              .map(
                (n) =>
                  `<option value="${esc(n)}" ${filterState.customer === n ? "selected" : ""}>${esc(n)}</option>`
              )
              .join("")}
          </select></div>
      </div>
      ${
        multiBiz
          ? `<div class="field"><label>Business</label><select name="business"><option value="">All</option>${businessNames
              .map(
                (n) =>
                  `<option value="${esc(n)}" ${filterState.business === n ? "selected" : ""}>${esc(n)}</option>`
              )
              .join("")}</select></div>`
          : ""
      }
      <div class="field-row">
        <div class="field"><label>From</label><input type="date" name="from" value="${filterState.from || ""}"></div>
        <div class="field"><label>To</label><input type="date" name="to" value="${filterState.to || ""}"></div>
      </div>
      <div class="btn-row">
        <button type="submit" class="btn small secondary">Apply filters</button>
        <button type="button" class="btn small ghost" id="clear-filters">Clear</button>
      </div>
    </form>
    ${
      totalCount
        ? `${renderGroup("Not sent yet", "not_sent", groups.not_sent, settings, multiBiz, quotes, activeFilterCount > 0)}
           ${renderGroup(`Sent, awaiting payment (${formatMoney(sentTotal)} owing)`, "sent", groups.sent, settings, multiBiz, quotes, activeFilterCount > 0)}
           ${renderGroup("Paid", "paid", groups.paid, settings, multiBiz, quotes, activeFilterCount > 0)}`
        : emptyStateHtml(
            "No invoices yet",
            "Create a sales invoice for a customer.",
            "empty-create",
            "Create sales invoice"
          )
    }`;

  panel.querySelector("#new-invoice")?.addEventListener("click", () => {
    clearDocReturnTo();
    router.navigate("invoices", "create");
  });
  panel.querySelector("#empty-create")?.addEventListener("click", () => {
    clearDocReturnTo();
    router.navigate("invoices", "create");
  });
  panel.querySelector("#toggle-filters")?.addEventListener("click", () => {
    filtersOpen = !filtersOpen;
    renderInvoices(panel, ctx);
  });
  panel.querySelector("#invoice-filters")?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    filterState = Object.fromEntries(fd.entries()) as InvoiceFilters;
    renderInvoices(panel, ctx);
  });
  panel.querySelector("#clear-filters")?.addEventListener("click", () => {
    filterState = {};
    renderInvoices(panel, ctx);
  });
  wireListActions(panel, ctx, customers);
}

function renderGroup(
  title: string,
  key: string,
  items: Record<string, unknown>[],
  settings: Record<string, unknown>,
  multiBiz: boolean,
  quotes: Record<string, Record<string, unknown>>,
  forceOpen = false
) {
  if (!items.length) {
    return `<details class="invoice-group"><summary>${title} (0)</summary><p class="hint">None</p></details>`;
  }
  const open = forceOpen || key !== "paid";
  return `<details class="invoice-group" ${open ? "open" : ""}>
    <summary>${title} (${items.length})</summary>
    ${items
      .map((inv) =>
        invoiceSummaryCardHtml(inv, { settings, multiBiz, quotes, showActions: true })
      )
      .join("")}
  </details>`;
}

/** Wire open + action buttons for invoice summary cards inside `panel`. */
export function wireInvoiceCardActions(
  panel: HTMLElement,
  ctx: AppContext,
  opts: {
    customers: Record<string, Record<string, unknown>>;
    onRefresh: () => Promise<void>;
    openReturnTo?: { tab: string; sub?: string | null; name?: string };
  }
) {
  const returnTo = opts.openReturnTo ?? { tab: "invoices" };
  panel.querySelectorAll("[data-open-invoice]").forEach((card) => {
    card.addEventListener("click", (ev) => {
      if ((ev.target as HTMLElement | null)?.closest("[data-action]")) return;
      const id = (card as HTMLElement).dataset.openInvoice || "";
      if (id) openInvoiceDoc(id, returnTo);
    });
  });
  panel.querySelectorAll("[data-open-invoice] [data-action]").forEach((btn) => {
    btn.addEventListener("click", async (ev) => {
      ev.stopPropagation();
      const el = btn as HTMLElement;
      const act = el.dataset.action;
      const id = String(el.dataset.id || "").trim();
      const n = Number(el.dataset.n);
      try {
        if (act === "send" || act === "send-now") {
          let scheduledAt: string | undefined;
          if (act === "send") {
            const timing = await openSendTimingSheet({ title: "Send invoice", kindLabel: "invoice" });
            if (timing === null) return;
            scheduledAt = timing || undefined;
          }
          showToast(scheduledAt ? "Scheduling…" : "Sending…", "success");
          const sendResult = await queueEmailSend(id, { scheduledAt });
          trackEvent("send_invoice");
          await flushQueue(ctx.onSyncStatus);
          const demoMock = Boolean(sendResult.demo || ctx.account?.demo);
          if (sendResult.finalStatus === "scheduled") {
            showToast(
              `Invoice scheduled for ${formatScheduledAt(scheduledAt) || "later"}.`,
              "success"
            );
            void watchDocSendStatus("invoice", id).then((status) => {
              if (status === "sent") {
                showToast(
                  demoMock
                    ? "Demo can't send emails. Marked as sent."
                    : "Invoice sent.",
                  demoMock ? "warning" : "success"
                );
              } else if (status === "send_failed") {
                showToast("Send failed. Check the customer email and try again.", "error");
              }
            });
          } else if (sendResult.finalStatus === "sent") {
            showToast(
              demoMock
                ? "Demo can't send emails. Marked as sent."
                : "Invoice sent.",
              demoMock ? "warning" : "success"
            );
          } else if (sendResult.finalStatus === "send_failed") {
            showToast("Send failed. Check the customer email and try again.", "error");
          } else {
            void watchDocSendStatus("invoice", id).then((status) => {
              if (status === "sent") {
                showToast(
                  demoMock
                    ? "Demo can't send emails. Marked as sent."
                    : "Invoice sent.",
                  demoMock ? "warning" : "success"
                );
              } else if (status === "send_failed") {
                showToast("Send failed. Check the customer email and try again.", "error");
              }
            });
          }
        } else if (act === "cancel-schedule") {
          await cancelScheduledSend(id, "invoice");
          await flushQueue(ctx.onSyncStatus);
          showToast("Schedule cancelled.", "success");
        } else if (act === "paid") {
          await updateInvoiceStatus(id, "paid");
          await flushQueue(ctx.onSyncStatus);
          showToast("Marked as paid.", "success");
        } else if (act === "followup") {
          showToast("Sending follow-up…", "success");
          const followResult = await queueFollowupEmail(id);
          trackEvent("followup_invoice");
          await flushQueue(ctx.onSyncStatus);
          if (followResult.demo || ctx.account?.demo) {
            showToast("Demo can't send emails. Follow-up not delivered.", "warning");
          } else {
            showToast("Follow-up sent.", "success");
          }
        } else if (act === "goto-quote") {
          const q = String(el.dataset.q || "").trim();
          if (q) openQuotesFiltered(q);
          return;
        } else if (act === "more") {
          await handleMoreActions(ctx, id, n, opts.onRefresh);
          return;
        }
        await opts.onRefresh();
      } catch (ex) {
        showToast(ex instanceof Error ? ex.message : "Action failed.", "error");
      }
    });
  });
}

function wireListActions(
  panel: HTMLElement,
  ctx: AppContext,
  customers: Record<string, Record<string, unknown>>
) {
  wireInvoiceCardActions(panel, ctx, {
    customers,
    onRefresh: async () => {
      await renderInvoices(panel, ctx);
    },
    openReturnTo: { tab: "invoices" },
  });
}

async function handleMoreActions(
  ctx: AppContext,
  invoiceId: string,
  n: number,
  onRefresh: () => Promise<void>
) {
  const choice = await openSheet({
    title: `#${formatInvoiceNumber(n)}`,
    bodyHtml: `<p class="hint">Move status or remove this invoice.</p>`,
    actions: [
      { id: "not_sent", label: "Move to Not sent", className: "btn secondary" },
      { id: "sent", label: "Move to Sent", className: "btn secondary" },
      { id: "paid", label: "Move to Paid", className: "btn secondary" },
      { id: "delete", label: "Remove", className: "btn danger", confirms: true },
      { id: "cancel", label: "Cancel", className: "btn ghost" },
    ],
  });
  if (!choice || choice === "cancel") return;
  try {
    if (choice === "delete") {
      const ok = await confirmSheet(`Remove invoice #${formatInvoiceNumber(n)}?`, "Remove");
      if (!ok) return;
      await softDeleteInvoice(invoiceId);
      await flushQueue(ctx.onSyncStatus);
      showToast("Invoice removed.", "success");
    } else {
      await updateInvoiceStatus(invoiceId, choice);
      await flushQueue(ctx.onSyncStatus);
      showToast(`Moved to ${statusLabel(choice)}.`, "success");
    }
    await onRefresh();
  } catch (ex) {
    showToast(ex instanceof Error ? ex.message : "Action failed.", "error");
  }
}

async function renderCreate(panel: HTMLElement, ctx: AppContext) {
  const [businesses, customers, invoices, settings] = await Promise.all([
    cache.getBusinesses(),
    cache.getCustomers(),
    cache.getInvoices(),
    cache.getSettings(),
  ]);
  const bizNames = Object.keys(businesses);
  const defaultBiz =
    settings.default_business && businesses[String(settings.default_business)]
      ? String(settings.default_business)
      : bizNames[0] || "";
  const restoring = Boolean(draft?.customer_name || draft?.invoice_number);
  const selectedBiz =
    restoring && draft?.business_name && businesses[String(draft.business_name)]
      ? String(draft.business_name)
      : defaultBiz;
  const biz = businesses[selectedBiz] || {};
  const gstReg = anyBusinessGstRegistered(businesses);
  const suggested = suggestedInvoiceNumber(selectedBiz, biz, invoices);
  const selectedCustomer = restoring ? String(draft?.customer_name || "") : "";
  const invoiceNumberValue = restoring
    ? String(draft?.invoice_number ?? suggested)
    : String(suggested);
  const invoiceDateValue = restoring
    ? String(draft?.invoice_date || todayIso())
    : todayIso();
  const duePrefs = restoring
    ? {
        due_rule_type: String(draft?.due_rule_type || "net_days"),
        due_net_days: Number(draft?.due_net_days ?? 7),
        due_fixed_date: String(draft?.due_fixed_date || ""),
      }
    : duePrefsForNewInvoice(settings, todayIso());
  const dueSummary0 = invoiceDueSummary(
    invoiceDateValue,
    duePrefs.due_rule_type,
    duePrefs.due_net_days,
    duePrefs.due_fixed_date
  );
  // Ensure one-off customers from the draft stay selectable after edit → create
  if (selectedCustomer && !customers[selectedCustomer] && !ephemeralCustomers[selectedCustomer]) {
    ephemeralCustomers[selectedCustomer] = {
      email: draft?.customer_email || "",
      abn: draft?.customer_abn || "",
      address_line1: draft?.address_line1 || "",
      address_line2: draft?.address_line2 || "",
      suburb: draft?.suburb || "",
      state: draft?.state || "",
      postcode: draft?.postcode || "",
    };
  }
  const custNames = [
    ...new Set([...Object.keys(customers), ...Object.keys(ephemeralCustomers)]),
  ].sort();
  const invNumDisplay = formatInvoiceNumber(
    Number(invoiceNumberValue) || invoiceNumberValue
  );
  const dateDisplay = formatInvoiceDate(invoiceDateValue);

  const photoThumbs = draftPhotos
    .map(
      (src, i) =>
        `<div class="photo-thumb"><img src="${src}" alt="Work photo ${i + 1}"><button type="button" class="btn small ghost remove-photo" data-i="${i}">×</button></div>`
    )
    .join("");

  const dueSummaryText = collapsedDueSummaryText(
    duePrefs.due_rule_type,
    duePrefs.due_net_days,
    duePrefs.due_fixed_date,
    dueSummary0.due_date_fmt
  );

  const detailsBody = `
      ${
        bizNames.length > 1
          ? `<div class="field"><label>From</label><select name="business">${bizNames
              .map(
                (n) =>
                  `<option value="${esc(n)}" ${n === selectedBiz ? "selected" : ""}>${esc(n)}</option>`
              )
              .join("")}</select></div>`
          : `<input type="hidden" name="business" value="${esc(selectedBiz)}">`
      }
      <div class="inv-meta-row">
        <div class="inv-meta-number">
          <button type="button" class="inv-num-btn" id="inv-num-toggle" aria-expanded="false">
            <strong id="inv-num-label">Invoice #${esc(String(invNumDisplay))}</strong>
            <span class="inv-num-hint">Change</span>
          </button>
        </div>
        <div class="inv-meta-date">
          <span class="inv-date-label">Date</span>
          <span class="inv-date-value">${esc(dateDisplay)}</span>
          <input type="hidden" name="invoice_date" value="${esc(invoiceDateValue)}">
        </div>
      </div>
      <div class="inv-num-edit" id="inv-num-edit" hidden>
        <div class="field"><label for="invoice_number_input">Invoice number</label>
          <input name="invoice_number" id="invoice_number_input" value="${esc(invoiceNumberValue)}" inputmode="numeric"></div>
      </div>
      <p class="hint error-text" id="invoice-number-warn" hidden></p>
      <input type="hidden" name="invoice_number_default" value="${esc(String(suggested))}">`;

  const customerBody = `
      <div class="field-row align-end customer-pick-row">
        <div class="field flex-2 customer-select-field">
          <label class="sr-only" for="customer-select">Customer</label>
          <select name="customer" id="customer-select" required>
            <option value="">Select customer</option>
            ${custNames
              .map(
                (n) =>
                  `<option value="${esc(n)}" ${n === selectedCustomer ? "selected" : ""}>${esc(n)}</option>`
              )
              .join("")}
          </select>
        </div>
        <button type="button" class="btn small secondary" id="add-customer-inline">+ Add</button>
      </div>`;

  const linesBody = `
      <div id="line-items" class="line-items-stack"></div>
      <button type="button" class="btn small ghost" id="add-line">+ Add line</button>`;

  const notesBody = `<div class="field"><label>Notes (optional)</label><textarea name="comment" rows="2" placeholder="Shown on the invoice">${esc(draft?.comment || "")}</textarea></div>`;

  const photosBody = `
      <p class="hint">Optional, max 6</p>
      <input type="file" id="work-photos" class="sr-only" accept="image/*" multiple>
      <button type="button" class="btn small secondary" id="add-photos-btn">Add photos</button>
      <div class="photo-grid" id="photo-grid">${photoThumbs}</div>
      <p class="hint">${draftPhotos.length}/6 photos</p>`;

  panel.innerHTML = `
    <form id="create-invoice-form" class="panel" novalidate>
      ${panelHeaderHtml({ title: "Create sales invoice" })}
      ${formSectionHtml("Invoice details", detailsBody)}
      ${formSectionHtml("Customer", customerBody)}
      ${formSectionHtml("Line items", linesBody)}
      ${formSectionHtml(
        "Payment due",
        disclosureBlockHtml({
          id: "due-disclosure",
          summaryHtml: `<span id="due-summary-text">${esc(dueSummaryText)}</span>`,
          bodyHtml: dueRuleFieldsHtml(duePrefs, { showFixed: true }),
          defaultOpen: false,
          toggleLabel: "Adjust",
          toggleLabelOpen: "Done",
        })
      )}
      ${formSectionHtml("Notes", notesBody)}
      ${formSectionHtml("Work photos", photosBody)}
      <div class="preview-section totals-panel" id="live-totals"></div>
      <p class="error-text" id="form-error" hidden></p>
      ${formActionsHtml({ saveId: "save-btn", saveLabel: "Save invoice" })}
    </form>`;

  wireDueRuleToggles(panel);
  wireDisclosure(panel, "due-disclosure", { closedLabel: "Adjust", openLabel: "Done" });

  const form = panel.querySelector("#create-invoice-form") as HTMLFormElement;
  const guard = attachCustomGuard({
    isDirty: () => isCreateDraftDirty(form),
    clear: () => clearCreateDraft(),
  });
  form.addEventListener("input", () => guard.markDirty());
  form.addEventListener("change", () => guard.markDirty());

  const syncInvNumLabel = () => {
    const input = panel.querySelector("#invoice_number_input") as HTMLInputElement | null;
    const label = panel.querySelector("#inv-num-label");
    if (!input || !label) return;
    try {
      label.textContent = `Invoice #${formatInvoiceNumber(parseInvoiceNumberInput(input.value))}`;
    } catch {
      /* keep */
    }
  };

  const setInvNumEditOpen = (open: boolean) => {
    const edit = panel.querySelector("#inv-num-edit") as HTMLElement | null;
    const btn = panel.querySelector("#inv-num-toggle") as HTMLButtonElement | null;
    const hint = panel.querySelector(".inv-num-hint");
    if (edit) edit.hidden = !open;
    if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");
    if (hint) hint.textContent = open ? "Done" : "Change";
    if (!open) {
      const input = panel.querySelector("#invoice_number_input") as HTMLInputElement | null;
      const def = (panel.querySelector('[name="invoice_number_default"]') as HTMLInputElement)
        ?.value;
      if (input && def && !String(input.value || "").trim()) input.value = def;
      syncInvNumLabel();
      refreshNumberWarn();
    }
  };

  panel.querySelector("#inv-num-toggle")?.addEventListener("click", () => {
    const edit = panel.querySelector("#inv-num-edit") as HTMLElement | null;
    setInvNumEditOpen(Boolean(edit?.hidden));
  });

  const refreshNumberWarn = () => {
    const warn = panel.querySelector("#invoice-number-warn") as HTMLElement | null;
    const input = panel.querySelector("#invoice_number_input") as HTMLInputElement | null;
    if (!warn || !input) return;
    try {
      const num = parseInvoiceNumberInput(input.value);
      const bizName = String(new FormData(form).get("business") || selectedBiz);
      const conflicts = findInvoiceNumberConflicts(invoices, num, bizName);
      if (conflicts.active || conflicts.deleted) {
        const parts: string[] = [];
        if (conflicts.active) {
          parts.push(
            `${conflicts.active} active invoice${conflicts.active === 1 ? "" : "s"} already use this number`
          );
        }
        if (conflicts.deleted) {
          parts.push(
            `${conflicts.deleted} removed invoice${conflicts.deleted === 1 ? "" : "s"} used this number`
          );
        }
        warn.textContent = `${parts.join("; ")}. You can still reuse it. A new invoice will be created.`;
        warn.hidden = false;
      } else {
        warn.textContent = "";
        warn.hidden = true;
      }
    } catch {
      warn.textContent = "";
      warn.hidden = true;
    }
  };
  panel.querySelector("#invoice_number_input")?.addEventListener("input", () => {
    syncInvNumLabel();
    refreshNumberWarn();
  });
  form.querySelector('[name="business"]')?.addEventListener("change", () => {
    const bizSelect = form.querySelector('[name="business"]') as HTMLSelectElement | null;
    const bizName = bizSelect?.value || selectedBiz;
    const nextSuggested = suggestedInvoiceNumber(bizName, businesses[bizName] || {}, invoices);
    const defInput = form.querySelector('[name="invoice_number_default"]') as HTMLInputElement | null;
    const numInput = form.querySelector("#invoice_number_input") as HTMLInputElement | null;
    if (defInput) defInput.value = String(nextSuggested);
    if (numInput && String(numInput.value) === String(invoiceNumberValue)) {
      numInput.value = String(nextSuggested);
      syncInvNumLabel();
    }
    refreshNumberWarn();
  });
  refreshNumberWarn();

  const refreshDueSummary = () => {
    const fd = new FormData(form);
    const due = dueRuleFromFormData(Object.fromEntries(fd.entries()), settings);
    const invDate = String(fd.get("invoice_date") || todayIso());
    const sum = invoiceDueSummary(invDate, due.due_rule_type, due.due_net_days, due.due_fixed_date);
    const el = panel.querySelector("#due-summary-text");
    if (el) {
      el.textContent = collapsedDueSummaryText(
        due.due_rule_type,
        due.due_net_days,
        due.due_fixed_date,
        sum.due_date_fmt
      );
    }
  };
  form.addEventListener("change", refreshDueSummary);
  form.addEventListener("input", refreshDueSummary);

  const refreshLineList = () => {
    const stack = panel.querySelector("#line-items") as HTMLElement | null;
    if (!stack) return;
    stack.innerHTML = draftLines.map((line, i) => lineSummaryRowHtml(line, i, gstReg)).join("");
    wireLineSummaryList(panel, gstReg, () => {
      refreshLineList();
      updateTotals();
    });
  };

  const updateTotals = () => {
    try {
      const parsed = parseLineItems(readDraftLineRows(gstReg), gstReg);
      (panel.querySelector("#live-totals") as HTMLElement).innerHTML = totalsHtml(
        gstReg,
        parsed.taxableExGst,
        parsed.gstFreeExGst,
        parsed.gstAmount,
        parsed.totalIncGst
      );
    } catch {
      (panel.querySelector("#live-totals") as HTMLElement).innerHTML = "";
    }
  };

  refreshLineList();
  updateTotals();

  panel.querySelector("#add-line")?.addEventListener("click", async () => {
    const saved = await openLineItemSheet({ gstReg });
    if (!saved) return;
    draftLines.push(saved);
    refreshLineList();
    updateTotals();
  });

  const runSave = async () => {
    const fd = new FormData(form);
    const businessName = String(fd.get("business") || selectedBiz);
    const gstRegistered = gstReg;
    const parsed = parseLineItems(readDraftLineRows(gstRegistered), gstRegistered);
    const customerName = String(fd.get("customer") || "").trim();
    if (!customerName) throw new Error("Select a customer.");
    const custProfile = resolveCustomerProfile(customerName, customers);
    const invoiceNumber = parseInvoiceNumberInput(String(fd.get("invoice_number")));
    const due = dueRuleFromFormData(Object.fromEntries(fd.entries()), settings);
    const dueSummary = invoiceDueSummary(
      fd.get("invoice_date") || todayIso(),
      due.due_rule_type,
      due.due_net_days,
      due.due_fixed_date
    );
    const invoiceId = String(draft?.invoice_id || "").trim() || newInvoiceId();
    const toSave: Record<string, unknown> = {
      invoice_id: invoiceId,
      invoice_number: invoiceNumber,
      invoice_date: String(fd.get("invoice_date") || todayIso()),
      ...snapshotCustomerOntoInvoice(customerName, custProfile),
      business_name: businessName,
      line_items: parsed.items,
      description: parsed.items.map((i) => i.description).join("; "),
      amount_ex_gst: moneyStr(parsed.subtotal),
      gst_amount: moneyStr(parsed.gstAmount),
      total_inc_gst: moneyStr(parsed.totalIncGst),
      taxable_ex_gst: moneyStr(parsed.taxableExGst),
      gst_free_ex_gst: moneyStr(parsed.gstFreeExGst),
      gst_registered: gstRegistered,
      due_rule_type: due.due_rule_type,
      due_net_days: due.due_net_days,
      due_fixed_date: due.due_fixed_date,
      due_date: dueSummary.due_date_iso,
      comment: String(fd.get("comment") || "").trim(),
      work_photos_b64: [...draftPhotos],
      status: String(draft?.status || "not_sent"),
      pdf_status: "pending",
    };
    draft = toSave;
    const savedId = await saveInvoicePackage({
      invoice: toSave,
      businessName,
      usedNumber: invoiceNumber,
      settingsPartial: {
        last_due_rule_type: due.due_rule_type,
        last_due_net_days: due.due_net_days,
        last_due_fixed_date: due.due_fixed_date || "",
      },
      preparePdf: true,
    });
    await flushQueue(ctx.onSyncStatus);
    clearCreateDraft();
    guard.clear();
    router.navigate("invoices", "success", { name: savedId }, { docSuccessMode: "draftable" });
    await renderSuccess(panel, ctx);
  };

  wireFormScreen({
    panel,
    guard,
    errorEl: "#form-error",
    back: {
      button: "#back-btn",
      onBack: leaveCreateForm,
    },
    save: {
      button: "#save-btn",
      label: "Save invoice",
      onSave: runSave,
      // runSave already navigates to the PDF success screen
      navigateAfterSave: false,
    },
  });

  panel.querySelector("#add-photos-btn")?.addEventListener("click", () => {
    (panel.querySelector("#work-photos") as HTMLInputElement | null)?.click();
  });

  panel.querySelector("#work-photos")?.addEventListener("change", async (e) => {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;
    try {
      captureCreateFormState(panel);
      draftPhotos = await filesToWorkPhotos(input.files, draftPhotos, 6);
      input.value = "";
      await renderCreate(panel, ctx);
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Could not add photo.", "error");
    }
  });

  panel.querySelectorAll(".remove-photo").forEach((btn) => {
    btn.addEventListener("click", () => {
      captureCreateFormState(panel);
      const i = Number((btn as HTMLElement).dataset.i);
      draftPhotos.splice(i, 1);
      renderCreate(panel, ctx);
    });
  });

  panel.querySelector("#add-customer-inline")?.addEventListener("click", async () => {
    await openInlineCustomerSheet(panel, ctx);
  });
}

async function openInlineCustomerSheet(panel: HTMLElement, ctx: AppContext) {
  const result = await new Promise<"saved" | "once" | null>((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";
    overlay.innerHTML = `
      <div class="sheet sheet-tall" role="dialog" aria-modal="true">
        <div class="sheet-handle" aria-hidden="true"></div>
        <h3 class="sheet-title">Add customer</h3>
        <form id="inline-customer-form" class="sheet-body" novalidate>
          ${abnLookupFieldHtml({
            name: "abn",
            id: "inline_customer_abn",
            label: "ABN",
            optionalMark: true,
          })}
          <div class="field"><label>Name</label><input name="name" id="inline_customer_name" required></div>
          <div class="field"><label>Email</label><input name="email" type="email"></div>
          ${addressFieldsHtml("", {})}
          <p class="hint">Save keeps them in Customers. Use once is only for this invoice.</p>
          <p class="error-text" id="inline-cust-error" hidden></p>
        </form>
        <div class="sheet-actions sheet-actions-triple">
          <button type="button" class="btn secondary" data-act="cancel">Cancel</button>
          <button type="button" class="btn secondary" data-act="once">Use once</button>
          <button type="button" class="btn primary" data-act="save">Save</button>
        </div>
      </div>`;
    const sheet = overlay.querySelector(".sheet") as HTMLElement;
    const form = overlay.querySelector("#inline-customer-form") as HTMLFormElement;
    const abnLookup = wireAbnLookup({
      root: form,
      abnInput: form.querySelector("#inline_customer_abn") as HTMLInputElement,
      nameInput: form.querySelector("#inline_customer_name") as HTMLInputElement,
      stateSelect: form.querySelector('select[name="state"]') as HTMLSelectElement | null,
      postcodeInput: form.querySelector('input[name="postcode"]') as HTMLInputElement | null,
    });
    const close = (v: "saved" | "once" | null) => {
      overlay.remove();
      if (!document.querySelector(".sheet-overlay")) {
        document.body.classList.remove("sheet-open");
      }
      resolve(v);
    };
    wireSheetGrabDismiss(overlay, sheet, () => close(null));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close(null);
    });
    overlay.querySelector('[data-act="cancel"]')?.addEventListener("click", () => close(null));

    const readProfile = () => {
      const fd = new FormData(form);
      const name = String(fd.get("name") || "").trim();
      if (!name) throw new Error("Customer name is required.");
      const email = String(fd.get("email") || "").trim();
      const addr = normalizeAuAddress(readAddressFromForm(fd));
      const abn = fd.get("abn") ? normalizeAbn(String(fd.get("abn"))) : "";
      return { name, profile: { email, ...addr, abn, created_via: "inline" } as Record<string, unknown> };
    };

    const confirmAbnForProfile = async (name: string, profile: Record<string, unknown>) =>
      confirmAbnBeforeSave({
        abnRaw: String(profile.abn || ""),
        form: {
          name,
          state: String(profile.state || ""),
          postcode: String(profile.postcode || ""),
        },
        handle: abnLookup,
      });

    overlay.querySelector('[data-act="once"]')?.addEventListener("click", async () => {
      const err = overlay.querySelector("#inline-cust-error") as HTMLElement;
      err.hidden = true;
      try {
        const { name, profile } = readProfile();
        if (!(await confirmAbnForProfile(name, profile))) return;
        ephemeralCustomers[name] = profile;
        draft = draft || {};
        (draft as Record<string, unknown>)._preselectCustomer = name;
        close("once");
      } catch (ex) {
        err.textContent = ex instanceof Error ? ex.message : "Invalid details.";
        err.hidden = false;
      }
    });

    overlay.querySelector('[data-act="save"]')?.addEventListener("click", async () => {
      const err = overlay.querySelector("#inline-cust-error") as HTMLElement;
      err.hidden = true;
      try {
        const { name, profile } = readProfile();
        if (!(await confirmAbnForProfile(name, profile))) return;
        await upsertCustomer(name, profile);
        await flushQueue(ctx.onSyncStatus);
        delete ephemeralCustomers[name];
        draft = draft || {};
        (draft as Record<string, unknown>)._preselectCustomer = name;
        close("saved");
      } catch (ex) {
        err.textContent = ex instanceof Error ? ex.message : "Save failed.";
        err.hidden = false;
      }
    });
    document.body.appendChild(overlay);
  });

  if (result === "saved" || result === "once") {
    showToast(result === "saved" ? "Customer saved." : "Customer added for this invoice.", "success");
    captureCreateFormState(panel);
    const pre = String((draft as Record<string, unknown> | null)?._preselectCustomer || "");
    if (pre && draft) {
      draft.customer_name = pre;
      delete (draft as Record<string, unknown>)._preselectCustomer;
    }
    await renderCreate(panel, ctx);
  }
}

function lineSummaryRowHtml(line: DraftLine, index: number, gstReg: boolean): string {
  const total = draftLineAmount(line, gstReg);
  const totalText = total == null ? "—" : formatMoney(total);
  const desc = esc(line.description || "Untitled");
  const gstBadge = gstReg
    ? `<span class="line-summary-gst">${line.gst_applicable ? "GST applicable" : "GST-free"}</span>`
    : "";
  return `<article class="line-summary" data-id="${esc(line.id)}" data-index="${index}">
    <button type="button" class="line-drag-handle" aria-label="Reorder line" title="Drag to reorder">⋮⋮</button>
    <button type="button" class="line-summary-main line-summary-main-compact">
      <span class="line-summary-desc">${desc}</span>
      ${gstBadge}
      <span class="line-summary-total">${totalText}</span>
    </button>
    <button type="button" class="btn icon-btn remove-line" aria-label="Remove line" title="Remove line">×</button>
  </article>`;
}

function wireLineSummaryList(panel: HTMLElement, gstReg: boolean, onChange: () => void) {
  panel.querySelectorAll("#line-items .line-summary").forEach((el) => {
    const row = el as HTMLElement;
    const id = row.dataset.id || "";
    row.querySelector(".line-summary-main")?.addEventListener("click", async () => {
      const line = draftLines.find((l) => l.id === id);
      if (!line) return;
      const saved = await openLineItemSheet({ gstReg, initial: line });
      if (!saved) return;
      const idx = draftLines.findIndex((l) => l.id === id);
      if (idx >= 0) draftLines[idx] = saved;
      onChange();
    });
    row.querySelector(".remove-line")?.addEventListener("click", (e) => {
      e.stopPropagation();
      draftLines = draftLines.filter((l) => l.id !== id);
      onChange();
    });
  });
  wireLineReorder(panel, onChange);
}

function wireLineReorder(panel: HTMLElement, onChange: () => void) {
  const stack = panel.querySelector("#line-items") as HTMLElement | null;
  if (!stack) return;

  stack.querySelectorAll(".line-drag-handle").forEach((handleEl) => {
    const handle = handleEl as HTMLElement;
    handle.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      const row = handle.closest(".line-summary") as HTMLElement | null;
      if (!row) return;
      e.preventDefault();
      e.stopPropagation();

      let currentIndex = Number(row.dataset.index);
      let lastSwapY = e.clientY;
      handle.setPointerCapture(e.pointerId);
      row.classList.add("line-summary-dragging");

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        const dy = ev.clientY - lastSwapY;
        const rows = [...stack.querySelectorAll(".line-summary")] as HTMLElement[];
        if (dy > 28 && currentIndex < rows.length - 1) {
          stack.insertBefore(rows[currentIndex + 1], rows[currentIndex]);
          const tmp = draftLines[currentIndex];
          draftLines[currentIndex] = draftLines[currentIndex + 1];
          draftLines[currentIndex + 1] = tmp;
          currentIndex += 1;
          lastSwapY = ev.clientY;
          renumberSummaryDom(stack);
        } else if (dy < -28 && currentIndex > 0) {
          stack.insertBefore(rows[currentIndex], rows[currentIndex - 1]);
          const tmp = draftLines[currentIndex];
          draftLines[currentIndex] = draftLines[currentIndex - 1];
          draftLines[currentIndex - 1] = tmp;
          currentIndex -= 1;
          lastSwapY = ev.clientY;
          renumberSummaryDom(stack);
        }
      };

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        try {
          handle.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        row.classList.remove("line-summary-dragging");
        onChange();
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    });
  });
}

function renumberSummaryDom(stack: HTMLElement) {
  stack.querySelectorAll(".line-summary").forEach((el, i) => {
    (el as HTMLElement).dataset.index = String(i);
  });
}

async function openLineItemSheet(opts: {
  gstReg: boolean;
  initial?: DraftLine;
}): Promise<DraftLine | null> {
  const editing = Boolean(opts.initial);
  const init = opts.initial || {
    id: newDraftLineId(),
    description: "",
    qty: "1",
    amount: "",
    gst_applicable: true,
    amount_includes_gst: false,
  };
  const gstBlock = opts.gstReg
    ? `<label class="gst-line-toggle sheet-gst-toggle" title="GST applicable when on; GST-free when off">
        <input type="checkbox" name="item_gst_applicable" id="sheet-gst-applicable" ${
          init.gst_applicable ? "checked" : ""
        }>
        <span class="gst-toggle-text">${init.gst_applicable ? "GST applicable" : "GST-free"}</span>
        <span class="gst-toggle-switch" aria-hidden="true"></span>
      </label>`
    : "";
  const incExBlock = opts.gstReg
    ? `<div class="gst-inc-ex-seg" id="sheet-inc-ex" role="group" aria-label="Price includes GST or excludes GST">
        <button type="button" class="gst-inc-ex-btn" data-mode="ex" aria-pressed="${
          init.amount_includes_gst ? "false" : "true"
        }">Ex GST</button>
        <button type="button" class="gst-inc-ex-btn" data-mode="inc" aria-pressed="${
          init.amount_includes_gst ? "true" : "false"
        }">Inc GST</button>
      </div>`
    : "";

  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";
    overlay.innerHTML = `
      <div class="sheet sheet-tall" role="dialog" aria-modal="true">
        <div class="sheet-handle" aria-hidden="true"></div>
        <div class="sheet-header">
          <h3 class="sheet-title">${editing ? "Edit line" : "Add line"}</h3>
          ${gstBlock}
        </div>
        <form id="line-item-sheet-form" class="sheet-body" novalidate>
          <div class="field"><label for="sheet-item-desc">Description</label>
            <input name="item_description" id="sheet-item-desc" value="${esc(init.description)}" required></div>
          <div class="line-item-amounts">
            <div class="field"><label for="sheet-item-qty">Qty</label>
              <input name="item_qty" id="sheet-item-qty" inputmode="decimal" value="${esc(init.qty || "1")}"></div>
            <div class="field field-unit-amount">
              <div class="field-unit-amount-head">
                <label for="sheet-item-amount" id="sheet-amount-label">Unit amount</label>
                ${incExBlock}
              </div>
              <input name="item_amount" id="sheet-item-amount" inputmode="decimal" value="${esc(init.amount)}" required>
            </div>
            <div class="field"><label>Line total</label>
              <div class="line-item-subtotal" id="sheet-line-total" aria-live="polite">—</div>
              <div class="line-item-subtotal-ex" id="sheet-line-total-ex" hidden></div>
            </div>
          </div>
          <p class="error-text" id="line-sheet-error" hidden></p>
        </form>
        <div class="sheet-actions">
          <button type="button" class="btn secondary" data-act="cancel">Cancel</button>
          <button type="button" class="btn primary" data-act="save">Save</button>
        </div>
      </div>`;
    const sheet = overlay.querySelector(".sheet") as HTMLElement;
    const form = overlay.querySelector("#line-item-sheet-form") as HTMLFormElement;
    const close = (v: DraftLine | null) => {
      overlay.remove();
      resolve(v);
    };
    wireSheetGrabDismiss(overlay, sheet, () => close(null));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close(null);
    });
    overlay.querySelector('[data-act="cancel"]')?.addEventListener("click", () => close(null));

    let amountIncludesGst = Boolean(init.amount_includes_gst);
    const gstInput = overlay.querySelector("#sheet-gst-applicable") as HTMLInputElement | null;
    const gstText = overlay.querySelector(".gst-toggle-text");
    const amountLabel = overlay.querySelector("#sheet-amount-label") as HTMLElement | null;
    const incExSeg = overlay.querySelector("#sheet-inc-ex") as HTMLElement | null;

    const syncIncExUi = () => {
      const gstApplicable = opts.gstReg ? Boolean(gstInput?.checked ?? true) : false;
      if (incExSeg) {
        incExSeg.hidden = !gstApplicable;
        for (const btn of incExSeg.querySelectorAll(".gst-inc-ex-btn")) {
          const mode = (btn as HTMLElement).dataset.mode;
          const pressed = gstApplicable && ((mode === "inc") === amountIncludesGst);
          btn.setAttribute("aria-pressed", pressed ? "true" : "false");
        }
      }
      if (amountLabel) {
        if (!opts.gstReg) amountLabel.textContent = "Unit amount";
        else if (!gstApplicable) amountLabel.textContent = "Unit amount";
        else amountLabel.textContent = amountIncludesGst ? "Unit amount inc GST" : "Unit amount ex GST";
      }
    };

    const syncSheetTotal = () => {
      const qty = (form.querySelector('[name="item_qty"]') as HTMLInputElement)?.value;
      const amount = (form.querySelector('[name="item_amount"]') as HTMLInputElement)?.value;
      const gstApplicable = opts.gstReg ? Boolean(gstInput?.checked ?? true) : false;
      const preview = previewLineAmounts({
        amount: amount || "",
        qty: qty || "1",
        gstRegistered: opts.gstReg,
        gstApplicable,
        amountIncludesGst: gstApplicable && amountIncludesGst,
      });
      const el = overlay.querySelector("#sheet-line-total");
      const exEl = overlay.querySelector("#sheet-line-total-ex") as HTMLElement | null;
      if (el) el.textContent = preview == null ? "—" : formatMoney(preview.amountInc);
      if (exEl) {
        if (preview && opts.gstReg && gstApplicable) {
          exEl.hidden = false;
          exEl.textContent = `ex ${formatMoney(preview.amountEx)}`;
        } else {
          exEl.hidden = true;
          exEl.textContent = "";
        }
      }
    };

    form.addEventListener("input", syncSheetTotal);
    syncIncExUi();
    syncSheetTotal();

    const qtyInput = form.querySelector("#sheet-item-qty") as HTMLInputElement | null;
    qtyInput?.addEventListener("focus", () => {
      if (String(qtyInput.value || "").trim() === "1") qtyInput.value = "";
    });
    qtyInput?.addEventListener("blur", () => {
      if (!String(qtyInput.value || "").trim()) {
        qtyInput.value = "1";
        syncSheetTotal();
      }
    });

    gstInput?.addEventListener("change", () => {
      if (gstText) gstText.textContent = gstInput.checked ? "GST applicable" : "GST-free";
      syncIncExUi();
      syncSheetTotal();
    });
    overlay.querySelector(".sheet-gst-toggle")?.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
    });

    incExSeg?.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".gst-inc-ex-btn") as HTMLElement | null;
      if (!btn || !incExSeg.contains(btn)) return;
      amountIncludesGst = btn.dataset.mode === "inc";
      syncIncExUi();
      syncSheetTotal();
    });

    overlay.querySelector('[data-act="save"]')?.addEventListener("click", () => {
      const err = overlay.querySelector("#line-sheet-error") as HTMLElement;
      err.hidden = true;
      try {
        const fd = new FormData(form);
        const description = String(fd.get("item_description") || "").trim();
        const qty = String(fd.get("item_qty") || "1").trim() || "1";
        const amount = String(fd.get("item_amount") || "").trim();
        if (!description) throw new Error("Add a description.");
        if (!amount) throw new Error("Enter a unit amount.");
        const gstApplicable = opts.gstReg ? Boolean(gstInput?.checked ?? true) : true;
        const includesGst = opts.gstReg && gstApplicable && amountIncludesGst;
        parseLineItems(
          [
            {
              description,
              qty,
              amount,
              gst_free: opts.gstReg && !gstApplicable,
              amount_includes_gst: includesGst,
            },
          ],
          opts.gstReg
        );
        close({
          id: init.id,
          description,
          qty,
          amount,
          gst_applicable: gstApplicable,
          amount_includes_gst: includesGst,
        });
      } catch (ex) {
        err.textContent = ex instanceof Error ? ex.message : "Invalid line.";
        err.hidden = false;
      }
    });

    document.body.appendChild(overlay);
  });
}

function totalsHtml(
  gstReg: boolean,
  taxable: number,
  gstFree: number,
  gst: number,
  total: number
) {
  if (!gstReg) {
    return `<div class="preview-row"><span>Total</span><strong>${formatMoney(total)}</strong></div>`;
  }
  let html = "";
  if (taxable > 0 && gstFree > 0) {
    html += `<div class="preview-row"><span>Taxable ex GST</span><span>${formatMoney(taxable)}</span></div>`;
    html += `<div class="preview-row"><span>GST-free ex GST</span><span>${formatMoney(gstFree)}</span></div>`;
  }
  html += `<div class="preview-row"><span>GST (10%)</span><span>${formatMoney(gst)}</span></div>`;
  html += `<div class="preview-row"><span>Total inc GST</span><strong>${formatMoney(total)}</strong></div>`;
  return html;
}

async function renderSuccess(panel: HTMLElement, ctx: AppContext): Promise<void> {
  revokeSuccessPdfUrl();
  const key = String(router.params.name || "").trim();
  const [invoices, customers] = await Promise.all([
    cache.getInvoices(),
    cache.getCustomers(),
  ]);
  const inv = invoices[key];
  if (!inv) {
    router.navigate("invoices");
    return renderInvoices(panel, ctx);
  }
  const n = Number(inv.invoice_number);
  const customer = customers[String(inv.customer_name)] || {};
  const sendEmail = String(inv.customer_email || customer.email || "").trim();
  const hasEmail = Boolean(sendEmail);
  const canSend = ["not_sent", "send_failed"].includes(String(inv.status || "not_sent"));
  const sendLabel = !canSend ? "Already sent" : "Send to customer";
  const showCancel = getDocSuccessMode() === "draftable";
  const referenceHint = `<p class="hint">Ask them to use invoice number <strong>${formatInvoiceNumber(n)}</strong> as the bank transfer reference.</p>`;

  panel.innerHTML = `
    <section class="panel success-panel">
      <div class="panel-header">
        <div>
          <h2>Invoice saved</h2>
          <p class="hint">#${formatInvoiceNumber(n)} · ${formatMoney(Number(inv.total_inc_gst || 0))}</p>
        </div>
        <button type="button" class="btn small ghost" id="success-edit">Edit</button>
      </div>
      <div class="pdf-embed-wrap" id="pdf-embed-wrap">
        <p class="hint pdf-embed-loading" id="pdf-embed-loading">Preparing PDF…</p>
        <iframe class="pdf-embed" id="pdf-embed" title="Invoice PDF" hidden></iframe>
      </div>
      <p class="error-text" id="pdf-embed-error" hidden></p>
      ${docSuccessActionsHtml({
        docKind: "invoice",
        showCancel,
        sendLabel,
        sendDisabled: !hasEmail || !canSend,
        hasEmail,
        referenceHint,
      })}
    </section>`;

  let pdfFilename = `Invoice_${formatInvoiceNumber(n)}.pdf`;

  const leaveSuccess = () => {
    revokeSuccessPdfUrl();
    navigateDocReturnOr(() => router.navigate("invoices", null, {}, { skipPush: true }));
  };

  wireDocSuccessPanel({
    panel,
    onDone: leaveSuccess,
    onEdit: () => {
      revokeSuccessPdfUrl();
      loadInvoiceIntoCreateDraft(inv);
      router.navigate("invoices", "create", {}, { docSuccessMode: "draftable" });
    },
    onDownload: () => {
      if (!successPdfUrl) {
        showToast("PDF not ready yet.", "error");
        return;
      }
      downloadPdf(successPdfUrl, pdfFilename);
    },
    onSend: async () => {
    try {
      const timing = await openSendTimingSheet({ title: "Send invoice", kindLabel: "invoice" });
      if (timing === null) return;
      const scheduledAt = timing || undefined;
      showToast(scheduledAt ? "Scheduling…" : "Sending…", "success");
      const sendResult = await queueEmailSend(key, { scheduledAt });
      trackEvent("send_invoice");
      await flushQueue(ctx.onSyncStatus);
      const demoMock = Boolean(sendResult.demo || ctx.account?.demo);
      if (sendResult.finalStatus === "scheduled") {
        showToast(
          `Invoice scheduled for ${formatScheduledAt(scheduledAt) || "later"}.`,
          "success"
        );
        void watchDocSendStatus("invoice", key).then((status) => {
          if (status === "sent") {
            showToast(
              demoMock
                ? "Demo can't send emails. Marked as sent."
                : "Invoice sent.",
              demoMock ? "warning" : "success"
            );
          } else if (status === "send_failed") {
            showToast("Send failed. Check the customer email and try again.", "error");
          }
        });
        leaveSuccess();
        return;
      }
      if (sendResult.finalStatus === "send_failed") {
        showToast("Send failed. Check the customer email and try again.", "error");
        return;
      }
      if (sendResult.finalStatus === "sent") {
        showToast(
          demoMock
            ? "Demo can't send emails. Marked as sent."
            : "Invoice sent.",
          demoMock ? "warning" : "success"
        );
        leaveSuccess();
        return;
      }
      revokeSuccessPdfUrl();
      navigateDocReturnOr(() => router.navigate("invoices"));
      void watchDocSendStatus("invoice", key).then((status) => {
        if (status === "sent") {
          showToast(
            demoMock
              ? "Demo can't send emails. Marked as sent."
              : "Invoice sent.",
            demoMock ? "warning" : "success"
          );
        } else if (status === "send_failed") {
          showToast("Send failed. Check the customer email and try again.", "error");
        }
      });
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Send failed.", "error");
    }
    },
    onCancelDoc: async () => {
      const ok = await confirmSheet(`Cancel invoice #${formatInvoiceNumber(n)}? This removes it.`, "Cancel invoice");
      if (!ok) return;
      await softDeleteInvoice(key);
      await flushQueue(ctx.onSyncStatus);
      showToast("Invoice cancelled.", "success");
      leaveSuccess();
    },
  });

  const loading = panel.querySelector("#pdf-embed-loading") as HTMLElement | null;
  const iframe = panel.querySelector("#pdf-embed") as HTMLIFrameElement | null;
  const errEl = panel.querySelector("#pdf-embed-error") as HTMLElement | null;
  const downloadBtn = panel.querySelector("#success-download") as HTMLButtonElement | null;

  try {
    const pdf = await fetchPdfBlob(key);
    revokeSuccessPdfUrl();
    successPdfUrl = pdf.url;
    pdfFilename = pdf.filename;
    if (iframe) {
      iframe.src = pdf.url;
      iframe.hidden = false;
    }
    if (loading) loading.hidden = true;
    if (downloadBtn) downloadBtn.disabled = false;
  } catch (ex) {
    if (loading) loading.hidden = true;
    if (errEl) {
      errEl.textContent =
        friendlyPdfErrorMessage(ex instanceof Error ? ex.message : "Could not load PDF.");
      errEl.hidden = false;
    }
  }
}
